import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../../lib/api-client";

export interface Layout {
  imageUrl: string;
  originalWidth: number;
  originalHeight: number;
  aspectRatio: number;
  fileName: string;
  uploadedBy: string;
  yOffset: number;
  createdAt: string;
  updatedAt: string;
}

interface LayoutResponse {
  layout: Layout;
}

interface PresignedUrlRequest {
  fileName: string;
  contentType: string;
}

interface PresignedUrlResponse {
  presignedUrl: string;
  uploadPath: string;
}

interface UpdateLayoutRequest {
  imageUrl: string;
  originalWidth: number;
  originalHeight: number;
  aspectRatio: number;
  fileName: string;
  yOffset?: number;
}

/**
 * imageUrlをローカル開発環境用に変換する
 * S3パス（layouts/xxx.png）を/api/uploads/パスに変換
 */
function resolveImageUrl(imageUrl: string): string {
  // すでにhttp(s)://で始まる場合はそのまま返す
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }
  // ローカル開発環境ではAPIサーバーのuploadsエンドポイントを使用
  if (imageUrl.startsWith("layouts/")) {
    return `/api/uploads/${imageUrl}`;
  }
  return imageUrl;
}

/**
 * 現在のオフィスレイアウト情報を取得する
 */
export function useLayout() {
  return useQuery({
    queryKey: ["layout"],
    queryFn: async () => {
      const response = await apiGet<LayoutResponse>("/layout");
      const layout = response.layout;
      // imageUrlをローカル開発環境用に変換
      return {
        ...layout,
        imageUrl: resolveImageUrl(layout.imageUrl),
      };
    },
  });
}

/**
 * S3アップロード用のPresigned URLを取得する
 */
export function useGeneratePresignedUrl() {
  return useMutation({
    mutationFn: async (data: PresignedUrlRequest) => {
      return apiPost<PresignedUrlResponse>("/admin/layout/upload-url", data);
    },
  });
}

/**
 * オフィスレイアウト情報を更新する
 */
export function useUpdateLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateLayoutRequest) => {
      const response = await apiPut<LayoutResponse>("/admin/layout", data);
      return response.layout;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["layout"] });
      queryClient.invalidateQueries({ queryKey: ["seats"] });
    },
  });
}
